import { useQuery } from '@tanstack/react-query';
import { attributeKeysFromCache, availableTemplateTokens } from '@blogforge/shared';
import { api } from './api.js';
import { useAuthStore } from '../stores/authStore.js';

// ─────────────────────────────────────────────────────────────────
// Available `{{…}}` tokens for the active workspace
//
// The block editor offers merge fields in three places — the inline `{{`
// mention picker and the image/OTP inspectors — and they must all offer the
// same set: a document's own variables plus, **in an email**, the workspace's
// audience attributes, minus the reserved system names (which the build
// overwrites, so offering one would produce a token that silently does
// nothing). That rule lives in `availableTemplateTokens` in @blogforge/shared;
// this hook is only the data half of it.
//
// Audience attributes are opt-in per call site, because they are per-recipient
// values: a post or component is published as static HTML, so an attribute
// token there would be built literally. Only the email editor passes
// `includeAudienceAttributes`.
//
// The attributes themselves come from the workspace's cache — one row, one
// request — rather than from the lists that define them.
// ─────────────────────────────────────────────────────────────────

export interface UseAvailableTemplateTokensOptions {
  /**
   * Include the workspace's audience attributes. True only for email
   * documents; see the note above on why this is not the default.
   */
  includeAudienceAttributes?: boolean;
}

export interface AvailableTemplateTokens {
  /** Sorted, de-duplicated, reserved names removed. */
  tokens: string[];
  /** Attribute keys only, for surfaces that show them separately. */
  attributeKeys: string[];
  /** True while the cache is still loading. */
  isLoading: boolean;
}

/**
 * @param documentVariables keys of the document's own definitions
 *   (`emails.variable_definition`), when it has any.
 */
export function useAvailableTemplateTokens(
  documentVariables: readonly string[] = [],
  options: UseAvailableTemplateTokensOptions = {},
): AvailableTemplateTokens {
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const includeAudienceAttributes = options.includeAudienceAttributes === true;

  const { data, isLoading } = useQuery({
    queryKey: ['workspace-attributes', activeWorkspaceId],
    queryFn: () => api.workspaces.attributes(activeWorkspaceId!),
    // Not fetched at all where it cannot be used, so a post editor does not
    // pull the cache down for nothing.
    enabled: Boolean(activeWorkspaceId) && includeAudienceAttributes,
    // The cache is rewritten by the same mutation that changes it, which
    // invalidates this key — so it never needs to poll.
    staleTime: 60_000,
  });

  const attributeKeys = includeAudienceAttributes ? attributeKeysFromCache(data) : [];

  return {
    tokens: availableTemplateTokens({
      documentVariables,
      workspaceAttributes: attributeKeys,
    }),
    attributeKeys,
    isLoading: includeAudienceAttributes && isLoading,
  };
}
